import { supabase } from '../supabase';
import type { ProductTemplate } from './productTemplates';
import { useAuthStore } from '../../stores/authStore';
import { useTemplateStore } from '../../stores/templateStore';

export async function saveTemplate(template: ProductTemplate): Promise<void> {
  try {
    const user = useAuthStore.getState().user;
    if (!user) throw new Error('User not authenticated');

    // Prepare the template data for Supabase
    const templateData = {
      id: template.id,
      user_id: user.id,
      name: template.name,
      fields: template.fields,
      folder_id: template.folderId,
      order_position: template.order || 0,
      is_deleted: template.isDeleted || false,
      updated_at: new Date().toISOString()
    };

    console.log('Saving template to Supabase:', templateData);

    const { error } = await supabase
      .from('templates')
      .upsert(templateData);

    if (error) {
      console.error('Supabase error saving template:', error);
      throw error;
    }

    console.log('Template saved successfully');
  } catch (error) {
    console.error('Failed to save template:', error);
    throw new Error('テンプレートの保存に失敗しました');
  }
}

export async function getTemplates(): Promise<ProductTemplate[]> {
  try {
    const user = useAuthStore.getState().user;
    if (!user) return [];

    const { data, error } = await supabase
      .from('templates')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false });

    if (error) {
      // Check if the error is because the table doesn't exist
      if (error.message.includes('relation "public.templates" does not exist')) {
        console.warn('Templates table does not exist yet. Returning empty array.');
        return [];
      }
      throw error;
    }

    // Map the database records to ProductTemplate format
    return (data || []).map(record => ({
      id: record.id,
      name: record.name,
      folderId: record.folder_id,
      order: record.order_position,
      fields: record.fields,
      createdAt: record.created_at,
      updatedAt: record.updated_at,
      isDeleted: record.is_deleted
    }));
  } catch (error) {
    console.error('Failed to load templates:', error);
    // Return empty array instead of throwing to prevent app from crashing
    return [];
  }
}

export async function deleteTemplate(id: string): Promise<void> {
  try {
    // Hard delete the template instead of soft delete
    const { error } = await supabase
      .from('templates')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Failed to delete template:', error);
    throw new Error('テンプレートの削除に失敗しました');
  }
}